// Раскладка вида от первого лица и хвата в третьем лице по видам оружия.
//
// Система камеры (вид от первого лица): x — вперёд, y — влево, z — вверх,
// метры от глаза. Оружие ставится точкой хвата правой руки (grip_r — начало
// координат модели) в pos, затем поворачивается rot (градусы: крен, тангаж,
// рыскание). Руки ставятся в точки хвата модели (grip_r / grip_l из
// weapons.json) и поворачиваются шагами steps — по очереди вокруг осей
// оружия (так проще подбирать хват, чем углами Эйлера).

import { WeaponId } from "./catalog";

export type RotStep = ["x" | "y" | "z", number];

export interface HandRig {
  /** grip_r / grip_l модели или «cam» — точка в системе камеры (кулаки). */
  at: "grip_r" | "grip_l" | "cam";
  /** Смещение от точки (система оружия; для cam — система камеры). */
  off: [number, number, number];
  steps: RotStep[];
}

export interface FpRig {
  pos: [number, number, number];
  rot: [number, number, number];
  scale: number;
  handR: HandRig | null;
  handL: HandRig | null;
  /** Плечи за кадром (система камеры) — от них руки (IK через локоть). */
  elbowR: [number, number, number];
  elbowL: [number, number, number];
  /** Свой «полюс» левого локтя вместо ARM.poleL (пила: рука к верхней ручке сбоку). */
  poleL?: [number, number, number];
  /** Размер дульной вспышки (0 — нет). */
  flash: number;
}

/** Длины звеньев руки и «полюса» локтей (система камеры: куда уводится
 *  локоть). Общие для всех видов оружия. */
export const ARM = {
  forearm: 0.40,
  upperarm: 0.40,
  poleR: [-0.2, -0.7, -1] as [number, number, number],
  // Левый локоть — вниз под оружие: предплечье подходит к цевью снизу,
  // а не тянется прямой диагональю из угла экрана.
  poleL: [0.1, 0.3, -1] as [number, number, number],
};

const ELBOW_R: [number, number, number] = [-0.16, -0.27, -0.40];
const ELBOW_L: [number, number, number] = [0.0, 0.20, -0.50];

/** Хват правой рукой пистолетной рукоятки (рукоять чуть завалена назад). */
const R_PISTOL: HandRig = { at: "grip_r", off: [0, 0, 0], steps: [["y", 16]] };
/** Левая рука под цевьём: цевьё проходит сквозь кулак вдоль ствола (y 90),
 *  запястье уходит вниз-влево (z −150) — рука держит снизу, а не сверху. */
const L_FOREND: HandRig = { at: "grip_l", off: [-0.06, 0, -0.012], steps: [["y", 90], ["z", -150]] };

function twoHand(pos: [number, number, number], flash: number, extra?: Partial<FpRig>): FpRig {
  return {
    pos, rot: [-4, 2, 8], scale: 0.8,
    handR: R_PISTOL, handL: L_FOREND,
    elbowR: ELBOW_R, elbowL: ELBOW_L, flash,
    ...extra,
  };
}

export const FP_RIGS: Record<number, FpRig> = {
  [WeaponId.Fists]: {
    pos: [0, 0, 0], rot: [0, 0, 0], scale: 1,
    handR: { at: "cam", off: [0.40, -0.15, -0.15], steps: [["x", -65], ["y", -12], ["z", 14]] },
    handL: { at: "cam", off: [0.39, 0.16, -0.16], steps: [["x", 65], ["y", -12], ["z", -14]] },
    elbowR: ELBOW_R, elbowL: ELBOW_L, flash: 0,
  },
  [WeaponId.Chainsaw]: {
    pos: [0.34, -0.20, -0.30], rot: [0, -4, 8], scale: 0.9,
    handR: { at: "grip_r", off: [0, 0, 0], steps: [["y", 90]] },
    handL: { at: "grip_l", off: [0, 0, 0], steps: [["x", 90]] },
    elbowR: ELBOW_R, elbowL: [-0.14, 0.26, -0.40], poleL: [-0.1, 0.8, -1], flash: 0,
  },
  [WeaponId.Shotgun]: twoHand([0.31, -0.20, -0.22], 1.4),
  [WeaponId.Rifle]: twoHand([0.28, -0.19, -0.21], 1.0),
  [WeaponId.Sniper]: twoHand([0.31, -0.20, -0.21], 1.2),
  [WeaponId.Flamethrower]: twoHand([0.38, -0.22, -0.27], 0),
  [WeaponId.Laser]: twoHand([0.34, -0.20, -0.22], 0.6),
  [WeaponId.Rocket]: twoHand([0.60, -0.20, -0.30], 1.6, { scale: 0.75, rot: [-4, 2, 10] }),
  [WeaponId.Grenade]: {
    pos: [0.30, -0.16, -0.13], rot: [0, 0, 0], scale: 1,
    handR: { at: "grip_r", off: [-0.005, 0, -0.01], steps: [["y", -8]] },
    handL: null,
    elbowR: ELBOW_R, elbowL: ELBOW_L, flash: 0,
  },
};

/** Третье лицо: подгонка нашей модели к вшитому автомату солдата
 *  (кость weapone_bone). Ось ствола вшитого — −X его ноды, верх — UP_SIGN·Y. */
export const TP_SOLDIER = {
  upSign: 1,
  /** Точка хвата в системе ноды weapon (метры, после снятия масштаба). */
  grip: [0.159, -0.062, 0.0125] as [number, number, number],
};
